import { FormGroup } from '@angular/forms';
import { IAssetById } from '../interfaces/asset.interface';
import { IFileInfo } from '../interfaces/shared.interface';
import type { AddAssetFormModel } from './add-asset-form.model';

/** Response shape for `getAssetCode` (stessa-angular HTTP layer). */
export interface INewAssetCodeResponseBody {
  data: {
    newAssetCodes: string[];
  };
}

export function mapNewAssetCodeResponseToValue(response: INewAssetCodeResponseBody): string {
  return response.data.newAssetCodes[0];
}

export interface BulkUploadPopupPatch {
  uploadListType: string;
  files: File;
}

export function mapBulkUploadSelectionToPopupPatch(
  selectedUploadListType : string,
  fileInfo: IFileInfo
): BulkUploadPopupPatch {
  return {
    uploadListType: selectedUploadListType ,
    files: fileInfo.file,
  };
}

/** QR or similar: `[..., assetCode, projectId, checkoutText]` */
export interface QrVerifyCheckoutSegments {
  assetCode: string;
  projectId: string;
  checkOutComment: string;
}

export function mapQrSegmentsToVerifyAndCheckoutPatch(segments: string[]): QrVerifyCheckoutSegments | null {
  if (segments.length < 4) {
    return null;
  }
  return {
    assetCode: segments[1],
    projectId: segments[2],
    checkOutComment: segments[3],
  };
}

export function mapLoaneeEmailToFormValue(obj: { email: string }): string {
  return obj.email;
}

/**
 * Applies `IAssetById` to the add-asset form after `selectNewForm(assetType)` has run.
 * Mirrors legacy `authoFillForm` behavior (including `parseInt` for screen size and redundant root `ramUnit` / `diskSpaceUnit` attempts).
 */
export function applyIAssetAutofillToAddAssetForm(
  form: FormGroup<AddAssetFormModel>,
  asset: IAssetById,
  assetType: string
): void {
  form.get('assetCode')?.setValue(asset.assetCode);
  form.get('modelName')?.setValue(asset.modelName);
  form.get('laptopFields')?.get('ram')?.setValue(asset.ram);
  form.get('laptopFields')?.get('diskSpace')?.setValue(asset.diskSpace);
  form.get('laptopFields')?.get('diskSpaceUnit')?.setValue(asset.diskSpaceUnit);
  form.get('location')?.setValue(asset.location);
  form.get('laptopFields')?.get('processor')?.setValue(asset.processor);

  let serialNoField = '';
  if (assetType === 'Mobile Phone') {
    serialNoField = 'mobileFields';
  } else if (assetType === 'Laptop') {
    serialNoField = 'laptopFields';
  } else if (assetType === 'Monitor') {
    serialNoField = 'monitorFields';
  } else if (form.get('deviceFields')) {
    serialNoField = 'deviceFields';
  }

  form.get(serialNoField)?.get('ram')?.setValue(asset.ram);
  form.get(serialNoField)?.get('ramUnit')?.setValue(asset.ramUnit);
  form.get(serialNoField)?.get('serialNo')?.setValue(asset.serialNo);
  form.get('purchaseDate')?.setValue(asset.purchaseDate);
  form.get('laptopFields')?.get('operatingSystem')?.setValue(asset.operatingSystem);
  form.get('laptopFields')?.get('ramUnit')?.setValue(asset.ramUnit);
  form.get('mobileFields')?.get('ramUnit')?.setValue(asset.ramUnit);

  const screenSizeField = assetType === 'Monitor' ? 'monitorFields' : 'laptopFields';
  form.get(screenSizeField)?.get('screenSize')?.setValue(parseInt(String(asset.screenSize), 10));
  form.get(screenSizeField)?.get('screenSizeUnit')?.setValue(asset.screenSizeUnit);

  form.get('vendorName')?.setValue(asset.vendorName);
  form.get('projectName')?.setValue(asset.projectName);
  form.get('clientName')?.setValue(asset.clientName);
  form.get('warranty')?.setValue(asset.warranty);
  form.get('laptopFields')?.get('endPointSecurityId')?.setValue(asset.endPointSecurityId);
  /** Legacy no-op attempts on non-modeled root keys (see {@link AddAssetFormModel}). */
  (form as FormGroup).get('diskSpaceUnit')?.setValue(asset.diskSpaceUnit);
  (form as FormGroup).get('ramUnit')?.setValue(asset.ramUnit);
}
